import React from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import SettingsPage from 'src/pages/Settings';
import ManualLookup from 'src/pages/ManualLookup';
import Column from 'src/frontend-common/components/Column';
import LoginPage from 'src/pages/Login/LoginPage';
import AppContainer from './AppContainer';
import { AppsPage } from 'src/pages/Apps';
import { CompaniesPage } from 'src/pages/Companies';
import { UsersPage } from 'src/pages/Users';
import { CompanyPage } from 'src/pages/Company';
import { UserPage } from 'src/pages/User';
import UserGuard from 'src/components/UserGuard';
import { UserCompletePage } from 'src/pages/UserComplete';
import { RegistrationCompletePage } from 'src/pages/Login/RegistrationCompletePage';
import SignUpPage from 'src/pages/Login/SignUpPage';
import ForgotPasswordPage from 'src/pages/Login/ForgotPasswordPage';
import { ForgotPasswordCodeSentPage } from 'src/pages/Login/ForgotPasswordCodeSentPage';
import { PasswordExpiredPage } from 'src/pages/Login/PasswordExpiredPage';
import ErrorPage from 'src/pages/Login/ErrorPage';
import ConfirmSuccessPage from 'src/pages/Login/ConfirmSuccessPage';
import LoadingPage from 'src/pages/Login/LoadingPage';
import WelcomePage from 'src/pages/Welcome';
import Page404 from 'src/pages/Errors/404';
import ImportPage from 'src/pages/ImportPage';
import * as Sentry from '@sentry/react';
import VerificationCodePage from 'src/pages/Login/VerificationCode';
import ExportPage from 'src/pages/Export';
import Analytics from 'src/pages/Analytics';
import FeatureAnalysis from 'src/pages/FeatureAnalysis';
import CustomPricing from 'src/pages/BillingAndPlan';
import RuleEngineWrapper from 'src/pages/RuleEngine';
import CustomerDetailPage from 'src/pages/CustomerDetail';
import Insights from 'src/pages/InsightsPage';
import { paths } from 'src/frontend-common/components/Router';
import 'src/main.css';
import Lookups from 'src/pages/Lookups';
import LoginSimulatorLanding from 'src/pages/LoginSimulatorLanding';
import Utilities from 'src/pages/Utilities';
import IdentityPage from 'src/pages/IdentityGraph';
import { AdminDashboardPage } from 'src/pages/AdminDashboard';

const App: React.FC = () => {
  const navigate = useNavigate();
  const navigateCallback = (destination: string) => {
    navigate(destination);
  };
  return (
    <AppContainer>
      <Column style={{ minHeight: '100vh' }}>
        <Routes>
          <Route path="/login-simulator/:username?/:pw?/:key?" element={<LoginSimulatorLanding navigateTo={navigateCallback} />} />

          <Route
            path="/"
            element={
              <UserGuard>
                <WelcomePage />
              </UserGuard>
            }
          />

          <Route path={paths.login.routerPath} element={<LoginPage />} />
          <Route path={paths.loading.routerPath} element={<LoadingPage />} />
          <Route path={paths.signUp.routerPath} element={<SignUpPage />} />
          <Route path={paths.passwordExpired.routerPath} element={<PasswordExpiredPage />} />
          <Route
            path="/welcome-page"
            element={
              <UserGuard>
                <WelcomePage />
              </UserGuard>
            }
          />
          <Route path={paths.authError.routerPath} element={<ErrorPage />} />
          <Route path={paths.registrationComplete.routerPath} element={<RegistrationCompletePage />} />
          <Route path={paths.forgotPassword.routerPath} element={<ForgotPasswordPage />} />
          <Route path={paths.verificationCode.routerPath} element={<VerificationCodePage payload={{ bah: '' }} />} />
          <Route path={paths.confirmSuccess.routerPath} element={<ConfirmSuccessPage />} />
          <Route path={paths.forgotPasswordSent.routerPath} element={<ForgotPasswordCodeSentPage />} />
          <Route
            path={paths.identityGraph.routerPath}
            element={
              <UserGuard>
                <IdentityPage navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route path={paths.completeUser.routerPath} element={<UserCompletePage />} />
          <Route
            path={paths.customPricing.routerPath}
            element={
              <UserGuard adminOnly={true}>
                <CustomPricing />
              </UserGuard>
            }
          />
          <Route
            path={paths.utilities.routerPath}
            element={
              <UserGuard adminOnly={true}>
                <Utilities />
              </UserGuard>
            }
          />

          {/* old path for backward compatibility */}
          <Route
            path={paths.customerDetails.routerPath}
            element={
              <UserGuard>
                <CustomerDetailPage product="onboarding" navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route
            path={paths.customerPhoneDetails.routerPath}
            element={
              <UserGuard>
                <CustomerDetailPage product="phone" navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route
            path={paths.customerEmailDetails.routerPath}
            element={
              <UserGuard>
                <CustomerDetailPage product="email" navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route
            path={paths.customerIpAddressDetails.routerPath}
            element={
              <UserGuard>
                <CustomerDetailPage product="ip" navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          {/* end */}

          <Route
            path={paths.customerDetailPage.routerPath}
            element={
              <UserGuard>
                <CustomerDetailPage product="onboarding" navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route
            path={paths.customerPhoneDetailPage.routerPath}
            element={
              <UserGuard>
                <CustomerDetailPage product="phone" navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route
            path={paths.customerEmailDetailPage.routerPath}
            element={
              <UserGuard>
                <CustomerDetailPage product="email" navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route
            path={paths.customerIpDetailPage.routerPath}
            element={
              <UserGuard>
                <CustomerDetailPage product="ip" navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route
            path={paths.customerDomainDetailPage.routerPath}
            element={
              <UserGuard>
                <CustomerDetailPage product="domain" navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route
            path={paths.customerLoginEnrollmentDetailPage.routerPath}
            element={
              <UserGuard>
                <CustomerDetailPage product="enrollment" navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route
            path={paths.customerLoginDetailPage.routerPath}
            element={
              <UserGuard>
                <CustomerDetailPage product="login" navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route
            path={paths.customerBotDetectionDetailPage.routerPath}
            element={
              <UserGuard>
                <CustomerDetailPage product="bot-detection" navigateTo={navigateCallback} />
              </UserGuard>
            }
          />

          <Route
            path={paths.lookups.routerPath}
            element={
              <UserGuard>
                <Lookups navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route
            path={paths.analytics.routerPath}
            element={
              <UserGuard>
                <Analytics navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route
            path={paths.lookup.routerPath}
            element={
              <UserGuard>
                <ManualLookup navigateTo={navigateCallback} />
              </UserGuard>
            }
          />
          <Route
            path={paths.insights.routerPath}
            element={
              <UserGuard>
                <Insights />
              </UserGuard>
            }
          />
          <Route
            path={paths.import.routerPath}
            element={
              <UserGuard>
                <ImportPage />
              </UserGuard>
            }
          />
          <Route
            path={`${paths.featureDetails.routerPath}/:id`}
            element={
              <UserGuard>
                <FeatureAnalysis />
              </UserGuard>
            }
          />
          <Route
            path={`${paths.export.routerPath}`}
            element={
              <UserGuard>
                <ExportPage />
              </UserGuard>
            }
          />
          <Route
            path={paths.ruleEngine.routerPath}
            element={
              <UserGuard>
                <RuleEngineWrapper />
              </UserGuard>
            }
          />
          <Route
            path={`${paths.ruleEngine.routerPath}/model/:id`}
            element={
              <UserGuard>
                <RuleEngineWrapper />
              </UserGuard>
            }
          />

          <Route
            path={`${paths.ruleEngine.routerPath}/rule/:modelId/:id`}
            element={
              <UserGuard>
                <RuleEngineWrapper />
              </UserGuard>
            }
          />
          <Route
            path={paths.appKeys.routerPath}
            element={
              <UserGuard>
                <AppsPage />
              </UserGuard>
            }
          />
          <Route
            path={paths.apps.routerPath}
            element={
              <UserGuard adminOnly={true}>
                <AppsPage />
              </UserGuard>
            }
          />
          <Route
            path={paths.companies.routerPath}
            element={
              <UserGuard adminOnly={true}>
                <CompaniesPage />
              </UserGuard>
            }
          />
          <Route
            path={paths.adminDashboard.routerPath}
            element={
              <UserGuard adminOnly={true}>
                <AdminDashboardPage />
              </UserGuard>
            }
          />
          <Route
            path={paths.companyDetail.routerPath}
            element={
              <UserGuard adminOnly={true}>
                <CompanyPage />
              </UserGuard>
            }
          />
          <Route
            path={`${paths.settings.routerPath}`}
            element={
              <UserGuard>
                <SettingsPage />
              </UserGuard>
            }
          />

          <Route
            path={`${paths.settings.routerPath}/:stripeSuccess?`}
            element={
              <UserGuard>
                <SettingsPage />
              </UserGuard>
            }
          />
          <Route
            path={paths.users.routerPath}
            element={
              <UserGuard adminOnly={true}>
                <UsersPage />
              </UserGuard>
            }
          />
          <Route
            path={paths.userDetail.routerPath}
            element={
              <UserGuard adminOnly={true}>
                <UserPage />
              </UserGuard>
            }
          />
          <Route
            path={paths.home.routerPath}
            element={
              <UserGuard>
                <Column data-cy="four-o-four" grow centered>
                  <Page404 />
                </Column>
              </UserGuard>
            }
          />
        </Routes>
      </Column>
    </AppContainer>
  );
};

export default Sentry.withErrorBoundary(App, {
  fallback: (e) => <Page404 error={e.error.message} />,
});
